import { useEffect, useState } from "react";
import _ from "underscore";

import usePrevious from "./use_previous";

import type { StateSetter } from "type_aliases";

/**
 * Allows having a state value that will update to match the prop value if the prop value changes (e.g. on page load)
 *
 * Most useful for when you want a field to be populated by data if/when the data loads, but to be otherwise editable.
 * @param trackedValue The value from props that the state value should match if it changes
 * @param initialValue An initial value for the state, just like you would pass in to {@link useState}
 * @param convertValueToState A callback that converts the trackedValue to the type that the state should be in - useful if you want to change its shape for state
 * @param deepComparison Whether to use deep comparison to decide if the tracked value has changed - useful for polled data
 */
const useStateTrackingValue = <TrackedType, Type = TrackedType>(trackedValue: TrackedType, initialValue?: Type, convertValueToState?: (value: TrackedType) => Type, deepComparison?: boolean): [Type, StateSetter<Type>] => {
  const [convertedValue, setConvertedValue] = useState<Type>(); // Using `useMemo` causes batched `setState` calls to not update the value when the converted value changes
  useEffect(() => {
    if (convertValueToState) {
      setConvertedValue(convertValueToState(trackedValue));
    } else {
      setConvertedValue(trackedValue as unknown as Type);
    }
  }, [trackedValue, convertValueToState]);

  const prevConvertedValue = usePrevious<Type>(convertedValue);

  const [value, setValue] = useState<Type>(initialValue);
  useEffect(() => {
    if (convertedValue == null && initialValue != null) {
      setValue(initialValue);
    } else {
      if (deepComparison) {
        if (!_.isEqual(prevConvertedValue, convertedValue)) {
          setValue(convertedValue);
        }
      } else {
        setValue(convertedValue);
      }
    }
    // Since initial value should never change, don't use it as a dependency
    // eslint-disable-next-line
  }, [convertedValue, deepComparison]);

  return [value, setValue];
};

export default useStateTrackingValue;
